<template>
  <div class="sidebar">
    <div
      :class="['sidebar__mobile--wrapper', { active: isActive }]"
      @click="quickLinksToggle"
    >
      <div class="sidebar__mobile-header">
        <h2>
          <span v-if="!isActive" class="sidebar__mobile--open">Open</span>
          <span v-else class="sidebar__mobile--open">Close</span>
          Sidebar
        </h2>
        <span class="quick-links--icon" />
      </div>
    </div>
    <SideBlockRenewal />
    <SideBlock region="sidebar_above_quicklinks" />

    <div v-if="$route.path === '/search'">
      <div class="section-info card">
        <h2>Search Files</h2>
      </div>
      <FilterDropdown
        type="tag"
        :items="tagsFiltered"
        dropdown-title="Select tags to filter"
        :selected-items="filtersStore.getTags"
      />
      <FilterDropdown
        type="topic"
        :items="topics"
        dropdown-title="Select topics to filter"
        :selected-items="filtersStore.getTopics"
      />
      <FilterDropdown
        type="content_type"
        :items="contentTypes"
        dropdown-title="Select content types to filter"
        :selected-items="filtersStore.getContentTypes"
      />
    </div>

    <div
      v-if="isUserLoggedIn && contentTypes"
      :class="['block block--green quick-link-block', { active: isActive }]"
    >
      <h2>Quick Links</h2>
      <ul class="quick-links">
        <li
          v-for="link in contentTypes"
          :key="link.id"
          class="quick-links__item"
          @click="resetActive"
        >
          <nuxt-link
            class="quick-links__link"
            :to="{ name: 'content-types-slug', params: { slug: link.slug } }"
          >
            <div
              v-if="link.coverSvg"
              class="item__icon"
              v-html="link.coverSvg"
            />
            {{ link.name }}
          </nuxt-link>
        </li>
      </ul>
    </div>

    <SideBlock region="sidebar_below_quicklinks" />

    <div :class="['block', { active: isActive }]"><TrustpilotWidget /></div>

    <div
      v-if="isUserLoggedIn && tagsCloud"
      :class="['block block--green', { active: isActive }]"
    >
      <h2>Tags</h2>
      <div class="tags-container">
        <nuxt-link
          v-for="link in tagsCloud"
          :key="link.id"
          class="tags__link"
          :to="{ name: 'tags-slug', params: { slug: link.slug } }"
          :style="{ fontSize: link.fontSize + 'px' }"
        >
          {{ link.name }}
        </nuxt-link>
      </div>
    </div>

    <SideBlock region="sidebar_below_tags" />
  </div>
</template>

<script setup lang="ts">
  import type { Item } from "@/types";
  const isActive = ref(false);

  const userStore = useUserStore();
  const pageContentStore = usePageContentStore();
  const filtersStore = useFiltersStore();

  const tagsCloud = computed(() => pageContentStore.getTagCloud);
  const tagsFiltered = computed(() => pageContentStore.getTags);
  const contentTypes = computed(() => pageContentStore.getContentTypes);
  const topics = computed(() => pageContentStore.getTopics);
  const isUserLoggedIn = computed(() => userStore.isUserLoggedIn);

  const quickLinksToggle = () => {
    isActive.value = !isActive.value;
  };

  const resetActive = () => {
    isActive.value = !isActive.value;
  };
</script>

<style lang="scss" scoped>
  .sidebar {
    width: 30%;
    margin-left: 30px;
    color: $white;

    h2 {
      color: $white;
    }
  }

  .quick-links__item {
    list-style: none;
  }

  .quick-links__link {
    display: flex;
    align-items: center;
    padding: 5px 0;
    color: $white;

    .item__icon {
      width: 25px;
      margin-right: 15px;
    }

    .u-badge {
      text-align: center;
      min-width: 1.2rem;
      margin-left: 1rem;
      text-decoration: none;
      height: 1.2rem;
      font-size: 0.75rem;
      line-height: 1;
    }

    &:hover {
      text-decoration: none;
      color: $pforange;
    }
  }

  .tags-container {
    text-align: center;
  }

  .tags__link {
    display: inline-block;
    margin: 0 5px;
    vertical-align: middle;
    color: $pforange;
    line-height: 1rem;
  }

  .sidebar__mobile--wrapper {
    display: none;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border: 3px solid $white;
    border-bottom: 0;

    h2 {
      color: white;
    }

    .quick-links--icon {
      position: relative;
      right: 0;
      top: 12px;
      width: 20px;
      height: 20px;
      transition: 0.3s all ease-in-out;

      &::before,
      &::after {
        position: absolute;
        content: "";
        display: block;
        top: 0;
        right: 10px;
        width: 20px;
        height: 4px;
        background: white;
        transition: 0.3s all ease-in-out;
      }

      &::after {
        transform: rotate(90deg);
      }
    }

    &.active {
      .quick-links--icon {
        &::before {
          transform: rotate(180deg);
        }

        &::after {
          transform: rotate(0deg);
        }
      }
    }
  }

  .sidebar__mobile-header {
    display: flex;
    justify-content: space-between;
    background: $pfgreen;
    width: 100%;
    padding: 20px 20px 0;
    margin-bottom: 20px;
  }

  @include respond-to-max("sm") {
    .sidebar__mobile--wrapper {
      display: flex;
    }

    .block {
      width: 100%;
      display: none;
      border-top: 0;
      transition: 0.3s all ease-in-out;

      &.active {
        display: block;
      }
    }
  }
</style>
